import React, { useState } from 'react';
import { X, Flag, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  postId: string;
  postContent: string;
  authorName: string;
}

const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onClose,
  postId,
  postContent,
  authorName
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reportReasons = [
    { value: 'inappropriate', label: 'Inappropriate Content', description: 'Content that violates community standards' },
    { value: 'spam', label: 'Spam', description: 'Unwanted promotional or repetitive content' },
    { value: 'abuse', label: 'Abusive Language', description: 'Offensive or harmful language' },
    { value: 'harassment', label: 'Harassment', description: 'Targeting or bullying behavior' },
    { value: 'hate_speech', label: 'Hate Speech', description: 'Content promoting hatred or discrimination' },
    { value: 'bullying', label: 'Bullying', description: 'Intimidating or threatening behavior' },
    { value: 'false_information', label: 'False Information', description: 'Misleading or false content' },
    { value: 'copyright', label: 'Copyright Violation', description: 'Unauthorized use of copyrighted material' },
    { value: 'other', label: 'Other', description: 'Other policy violations' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedReason) {
      toast.error('Please select a reason for reporting');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await api.post(`/reports/post/${postId}`, {
        reason: selectedReason,
        description: description.trim() || undefined
      });

      if (response.data.success) {
        toast.success('Report submitted successfully. Thank you for helping keep our community safe.');
        onClose();
        // Reset form
        setSelectedReason('');
        setDescription('');
      }
    } catch (error: any) {
      console.error('Report submission error:', error);
      
      if (error.response?.data?.error?.code === 'ALREADY_REPORTED') {
        toast.error('You have already reported this post');
      } else {
        toast.error(error.response?.data?.error?.message || 'Failed to submit report. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Flag className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-semibold text-gray-900">Report Post</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Post Preview */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Reporting post by <strong>{authorName}</strong>:</p>
            <p className="text-sm text-gray-800 line-clamp-3">
              {postContent.length > 100 ? `${postContent.substring(0, 100)}...` : postContent}
            </p>
          </div>

          {/* Warning */}
          <div className="flex items-start space-x-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">Before you report:</p>
              <p>Make sure this content violates our community guidelines. False reports may result in restrictions on your account.</p>
            </div>
          </div>

          {/* Reason Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Why are you reporting this post? *
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {reportReasons.map((reason) => (
                <label
                  key={reason.value}
                  className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedReason === reason.value
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="mt-1 text-red-600 focus:ring-red-500"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{reason.label}</p>
                    <p className="text-sm text-gray-600">{reason.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Description */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Additional details (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide any additional context that might help us review this report..."
              className="w-full h-20 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              maxLength={500}
            />
            <p className="text-xs text-gray-500">{description.length}/500 characters</p>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedReason || isSubmitting}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportModal;